import type { ReactElement, ReactNode } from 'react';
import { Link } from 'react-router-dom';

export interface MarkdownRendererProps {
  readonly markdown: string;
  readonly hideLeadingTitle?: string | undefined;
}

type ParsedBlock =
  | { readonly kind: 'heading'; readonly level: 1 | 2 | 3; readonly text: string }
  | { readonly kind: 'paragraph'; readonly text: string }
  | { readonly kind: 'blockquote'; readonly lines: readonly string[] }
  | { readonly kind: 'code'; readonly language: string; readonly code: string }
  | { readonly kind: 'ul'; readonly items: readonly string[] }
  | { readonly kind: 'ol'; readonly items: readonly string[] }
  | {
      readonly kind: 'table';
      readonly headers: readonly string[];
      readonly rows: readonly (readonly string[])[];
    }
  | { readonly kind: 'hr' };

function splitTableRow(line: string): readonly string[] {
  const trimmed = line.trim();
  const withoutOuterPipes = trimmed.replace(/^\|/, '').replace(/\|$/, '');
  return withoutOuterPipes.split('|').map((cell) => cell.trim());
}

function isTableSeparatorLine(line: string): boolean {
  const trimmed = line.trim();
  if (!trimmed.includes('|')) {
    return false;
  }
  const cells = splitTableRow(trimmed);
  return cells.length > 0 && cells.every((cell) => /^:?-{2,}:?$/.test(cell));
}

export function parseMarkdownBlocks(markdown: string): readonly ParsedBlock[] {
  const lines = markdown.replace(/\r\n/g, '\n').split('\n');
  const blocks: ParsedBlock[] = [];
  let index = 0;

  while (index < lines.length) {
    const rawLine = lines[index] ?? '';
    const trimmed = rawLine.trim();

    if (trimmed.length === 0) {
      index += 1;
      continue;
    }

    // Fenced code block
    if (trimmed.startsWith('```')) {
      const language = trimmed.slice(3).trim();
      const codeLines: string[] = [];
      index += 1;
      while (index < lines.length && !(lines[index] ?? '').trim().startsWith('```')) {
        codeLines.push(lines[index] ?? '');
        index += 1;
      }
      if (index < lines.length) {
        index += 1;
      }
      blocks.push({ kind: 'code', language, code: codeLines.join('\n') });
      continue;
    }

    // Horizontal rule
    if (/^(-{3,}|\*{3,})$/.test(trimmed)) {
      blocks.push({ kind: 'hr' });
      index += 1;
      continue;
    }

    // Headings
    const headingMatch = /^(#{1,3})\s+(.+)$/.exec(trimmed);
    if (headingMatch) {
      const hashes = headingMatch[1] ?? '#';
      const text = (headingMatch[2] ?? '').trim();
      const level = (Math.min(3, Math.max(1, hashes.length)) as 1 | 2 | 3) ?? 2;
      blocks.push({ kind: 'heading', level, text });
      index += 1;
      continue;
    }

    // Blockquote / callout
    if (trimmed.startsWith('>')) {
      const quoteLines: string[] = [];
      while (index < lines.length && (lines[index] ?? '').trim().startsWith('>')) {
        const cleaned = (lines[index] ?? '')
          .trim()
          .replace(/^>\s?/, '')
          .replace(/^\[!(NOTE|TIP|IMPORTANT|WARNING|CAUTION)\]\s*/i, '');
        if (cleaned.length > 0) {
          quoteLines.push(cleaned);
        }
        index += 1;
      }
      blocks.push({ kind: 'blockquote', lines: quoteLines });
      continue;
    }

    // GFM Table
    const nextLine = lines[index + 1] ?? '';
    if (trimmed.includes('|') && isTableSeparatorLine(nextLine)) {
      const headers = splitTableRow(trimmed);
      const rows: (readonly string[])[] = [];
      index += 2;
      while (index < lines.length) {
        const rowLine = (lines[index] ?? '').trim();
        if (rowLine.length === 0 || !rowLine.includes('|')) {
          break;
        }
        rows.push(splitTableRow(rowLine));
        index += 1;
      }
      blocks.push({ kind: 'table', headers, rows });
      continue;
    }

    // Unordered list
    if (/^[-*]\s+/.test(trimmed)) {
      const items: string[] = [];
      while (index < lines.length && /^[-*]\s+/.test((lines[index] ?? '').trim())) {
        items.push((lines[index] ?? '').trim().replace(/^[-*]\s+/, ''));
        index += 1;
      }
      blocks.push({ kind: 'ul', items });
      continue;
    }

    // Ordered list
    if (/^\d+\.\s+/.test(trimmed)) {
      const items: string[] = [];
      while (index < lines.length && /^\d+\.\s+/.test((lines[index] ?? '').trim())) {
        items.push((lines[index] ?? '').trim().replace(/^\d+\.\s+/, ''));
        index += 1;
      }
      blocks.push({ kind: 'ol', items });
      continue;
    }

    // Paragraph (join consecutive plain text lines)
    const paragraphLines: string[] = [trimmed];
    index += 1;
    while (index < lines.length) {
      const peek = (lines[index] ?? '').trim();
      if (
        peek.length === 0 ||
        peek.startsWith('#') ||
        peek.startsWith('>') ||
        peek.startsWith('```') ||
        /^[-*]\s+/.test(peek) ||
        /^\d+\.\s+/.test(peek) ||
        /^(-{3,}|\*{3,})$/.test(peek) ||
        (peek.includes('|') && isTableSeparatorLine(lines[index + 1] ?? ''))
      ) {
        break;
      }
      paragraphLines.push(peek);
      index += 1;
    }
    blocks.push({ kind: 'paragraph', text: paragraphLines.join(' ') });
  }

  return blocks;
}

export function renderInlineMarkdown(text: string, keyPrefix: string): readonly ReactNode[] {
  const nodes: ReactNode[] = [];
  const tokenRegex = /(\*\*[^*]+\*\*|`[^`]+`|\[[^\]]+\]\([^)]+\))/g;
  let lastIndex = 0;
  let match: RegExpExecArray | null = tokenRegex.exec(text);
  let partIdx = 0;

  while (match !== null) {
    if (match.index > lastIndex) {
      nodes.push(text.slice(lastIndex, match.index));
    }
    const token = match[0] ?? '';
    const nodeKey = `${keyPrefix}-${String(partIdx)}`;
    partIdx += 1;

    if (token.startsWith('**') && token.endsWith('**')) {
      nodes.push(
        <strong key={nodeKey} className="font-semibold text-fg">
          {token.slice(2, -2)}
        </strong>,
      );
    } else if (token.startsWith('`') && token.endsWith('`')) {
      nodes.push(
        <code
          key={nodeKey}
          className="rounded-control border border-subtle bg-surface-muted px-1.5 py-0.5 font-mono text-[12px] text-fg"
        >
          {token.slice(1, -1)}
        </code>,
      );
    } else if (token.startsWith('[')) {
      const linkMatch = /^\[([^\]]+)\]\(([^)]+)\)$/.exec(token);
      const label = linkMatch?.[1] ?? token;
      const href = linkMatch?.[2] ?? '';
      if (href.startsWith('/')) {
        nodes.push(
          <Link
            key={nodeKey}
            to={href}
            className="font-medium text-brand underline decoration-brand hover:opacity-80"
          >
            {label}
          </Link>,
        );
      } else {
        nodes.push(
          <a
            key={nodeKey}
            href={href}
            target="_blank"
            rel="noopener noreferrer"
            className="font-medium text-brand underline decoration-brand hover:opacity-80"
          >
            {label}
          </a>,
        );
      }
    } else {
      nodes.push(token);
    }

    lastIndex = match.index + token.length;
    match = tokenRegex.exec(text);
  }

  if (lastIndex < text.length) {
    nodes.push(text.slice(lastIndex));
  }

  return nodes;
}

export function MarkdownRenderer({
  markdown,
  hideLeadingTitle,
}: MarkdownRendererProps): ReactElement {
  const parsedBlocks = parseMarkdownBlocks(markdown);
  const blocks =
    hideLeadingTitle !== undefined &&
    parsedBlocks[0]?.kind === 'heading' &&
    parsedBlocks[0].level === 1 &&
    parsedBlocks[0].text.trim().toLowerCase() === hideLeadingTitle.trim().toLowerCase()
      ? parsedBlocks.slice(1)
      : parsedBlocks;

  return (
    <div className="space-y-4 text-[14px] leading-relaxed text-fg">
      {blocks.map((block, idx) => {
        const key = `md-block-${String(idx)}`;

        switch (block.kind) {
          case 'heading': {
            if (block.level === 1) {
              return (
                <h2 key={key} className="text-[20px] font-bold tracking-tight text-fg">
                  {renderInlineMarkdown(block.text, key)}
                </h2>
              );
            }
            if (block.level === 2) {
              return (
                <h3
                  key={key}
                  className="mt-6 border-b border-subtle pb-1.5 text-[16px] font-semibold tracking-tight text-fg"
                >
                  {renderInlineMarkdown(block.text, key)}
                </h3>
              );
            }
            return (
              <h4 key={key} className="mt-4 text-[14px] font-semibold text-fg">
                {renderInlineMarkdown(block.text, key)}
              </h4>
            );
          }

          case 'paragraph':
            return (
              <p key={key} className="text-[14px] leading-relaxed text-fg-secondary">
                {renderInlineMarkdown(block.text, key)}
              </p>
            );

          case 'blockquote': {
            const joined = block.lines.join(' ');
            const isWarningOrPlaceholder = /placeholder|legal review|coming soon|warning/i.test(
              joined,
            );
            return (
              <div
                key={key}
                role="note"
                className={`rounded-card border px-4 py-3 text-[13px] ${
                  isWarningOrPlaceholder
                    ? 'border-brand bg-status-warning-bg text-fg'
                    : 'border-brand bg-brand-wash text-fg'
                }`}
              >
                <div className="space-y-1.5">
                  {block.lines.map((line, lineIdx) => (
                    <p key={`${key}-q-${String(lineIdx)}`}>
                      {renderInlineMarkdown(line, `${key}-q-${String(lineIdx)}`)}
                    </p>
                  ))}
                </div>
              </div>
            );
          }

          case 'code':
            return (
              <pre
                key={key}
                className="overflow-x-auto rounded-card border border-border bg-surface-muted p-4 font-mono text-[12px] leading-relaxed text-fg"
              >
                <code>{block.code}</code>
              </pre>
            );

          case 'ul':
            return (
              <ul key={key} className="list-disc space-y-1.5 pl-5 text-[14px] text-fg-secondary">
                {block.items.map((item, itemIdx) => (
                  <li key={`${key}-li-${String(itemIdx)}`}>
                    {renderInlineMarkdown(item, `${key}-li-${String(itemIdx)}`)}
                  </li>
                ))}
              </ul>
            );

          case 'ol':
            return (
              <ol key={key} className="list-decimal space-y-1.5 pl-5 text-[14px] text-fg-secondary">
                {block.items.map((item, itemIdx) => (
                  <li key={`${key}-oli-${String(itemIdx)}`}>
                    {renderInlineMarkdown(item, `${key}-oli-${String(itemIdx)}`)}
                  </li>
                ))}
              </ol>
            );

          case 'table':
            return (
              <div key={key} className="overflow-x-auto rounded-card border border-border">
                <table className="w-full border-collapse text-left text-[13px]">
                  <thead>
                    <tr className="border-b border-border bg-surface-muted">
                      {block.headers.map((header, hIdx) => (
                        <th
                          key={`${key}-th-${String(hIdx)}`}
                          className="px-3.5 py-2.5 font-semibold text-fg"
                        >
                          {renderInlineMarkdown(header, `${key}-th-${String(hIdx)}`)}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-subtle bg-surface">
                    {block.rows.map((row, rIdx) => (
                      <tr key={`${key}-tr-${String(rIdx)}`}>
                        {row.map((cell, cIdx) => (
                          <td
                            key={`${key}-td-${String(rIdx)}-${String(cIdx)}`}
                            className="px-3.5 py-2.5 align-top text-fg-secondary"
                          >
                            {renderInlineMarkdown(
                              cell,
                              `${key}-td-${String(rIdx)}-${String(cIdx)}`,
                            )}
                          </td>
                        ))}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            );

          case 'hr':
            return <hr key={key} className="border-subtle" />;
        }
      })}
    </div>
  );
}
